import { FC, memo } from 'react';
import dynamic from 'next/dynamic';
import Grid from '@material-ui/core/Grid';
import styled from 'styled-components';

import { VideosSection } from '../videosSection';
import { PropertyDetails } from '@keaze/web/common/interfaces';
import { FAQ_DATA } from '@keaze/web/utils/data/faq';
import { HousingProvider } from './housingProvider';
import { Features } from './features';
import { LeftSide } from '../listingDetails.styles';
import { FAQScheme } from 'microdataSchemas';
import { PlanModalProps } from '../planModal';
import { SinglePropertyProps } from './singleProperty.types';
import {
  convertBrochuresToDownloadFiles,
  convertFloorPlansToPlans,
  convertFloorPlansToSpecifications,
} from '../helpers/converts';
import { generatePropertyType } from '@keaze/web/utils/generatePropertyType';
import {
  StyledOverview,
  StyledGroupDownloadFile,
  StyledGroupAccordion,
  MobileStartRegistration,
  DesktopStartRegistration,
  SinglePropertyBreakdown,
} from './singleProperty.styles';

const S = {
  Disclaimer: styled.p`
    font-size: 12px;
    color: #003c3b;
  `,
  mobileReg: styled(MobileStartRegistration)`
    ul {
      display: none;
    }
    ${({ theme: { breakpoints } }) => `
    display: block;
    border: 1px solid #672500;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    top: 1rem;
    margin: auto;
    width: auto;
    height: auto;
    box-shadow: 0px -2px 4px 0px rgba(0, 0, 0, 0.25);
    border-radius: 0;
    button {
      background-color: #672500;
      width: 214px;
      height: 41px;
      bottom: 15px;
    }
    ${breakpoints.up('xs')} { 
width: auto;

    }
    ${breakpoints.up('sm')} { 
      width: 90%;
      
          }
  ${breakpoints.up('md')} {
    display: none;
  }
`}
  `,
};

const DynamicPlanModal = dynamic<PlanModalProps>(
  () => import('../planModal').then((model) => model.PlanModal),
  { ssr: false }
);

export const SingleProperty: FC<SinglePropertyProps> = memo(
  ({
    listing,
    address,
    spacing,
    isRegisterInterestLoading,
    isListingLoading,
    onRegisterButtonClick,
    onVideoClick,
  }) => {
    const {
      videos,
      longDescription,
      housingProvider,
      propertyFeatures,
      propertyDetails,
      brochures,
    } = listing;
    const customFloorPlans = propertyDetails ? propertyDetails.floorplans : [];
    const customFullSpecifications = propertyDetails
      ? propertyDetails.fullSpecifications
      : null;
    const brochuresDownloadFiles = convertBrochuresToDownloadFiles(brochures);
    const isBrochuresDownloadFiles = brochuresDownloadFiles.length !== 0;
    const overviewTitle = generatePropertyType(
      listing.bedrooms,
      listing.propertyType
    );

    return (
      <>
        <Grid container spacing={spacing}>
          <Grid item xs={12} md={7}>
            <LeftSide>
              <VideosSection
                listing={listing}
                description={address}
                videos={videos}
                specifications={convertFloorPlansToSpecifications(
                  customFloorPlans
                )}
                onVideoClick={onVideoClick}
              />
              <S.mobileReg
                listing={listing}
                isLoading={isRegisterInterestLoading}
                isListingLoading={isListingLoading}
                onRegisterButtonClick={onRegisterButtonClick}
              />

              <StyledOverview
                title={overviewTitle}
                description={longDescription ?? ''}
              />
              <Features
                propertyFeatures={propertyFeatures}
                propertyDetails={propertyDetails as PropertyDetails}
              />
            </LeftSide>
          </Grid>
          <Grid item xs={12} md={5}>
            <HousingProvider housingProvider={housingProvider} />
            <SinglePropertyBreakdown
              listing={listing}
              isLoading={isRegisterInterestLoading}
              isListingLoading={isListingLoading}
              onRegisterButtonClick={onRegisterButtonClick}
            />
            {isBrochuresDownloadFiles && (
              <StyledGroupDownloadFile
                title="Brochures"
                data={brochuresDownloadFiles}
              />
            )}
            <StyledGroupAccordion
              title="FAQs"
              data={FAQ_DATA}
              microdataScheme={FAQScheme}
            />
            <S.Disclaimer>Disclaimer</S.Disclaimer>
            <S.Disclaimer>
              The figures and estimates shown in this plot illustration should
              not be relied on as confirmation of affordability and should only
              be used as an indicative guide to monthly costs. The monthly
              rental cost is based on 2.75% on the retained equity (subject to
              change please ask your sales agent). This will usually increase by
              the annual rate of RPI or CPI. The monthly mortgage cost is based
              on a Variable Rate Mortgage at 5.50% over a 25-year repayment loan
              at a 95% LTV. Other costs will apply such as service charge. Sage
              Homes is not authorised or regulated by the Financial Conduct
              Authority to provide financial advice. You should always seek
              advice and recommendations from an independent financial advisor
              regarding mortgage products, affordability, interest rates,
              repayment methods and terms and conditions. You should also seek
              independent legal advice. Your home may be repossessed if you do
              not keep up payments on a mortgage or any other loan secured
              against it.
            </S.Disclaimer>
          </Grid>
        </Grid>

        <DynamicPlanModal
          listing={listing}
          description={address}
          data={convertFloorPlansToPlans(
            customFloorPlans,
            customFullSpecifications
          )}
        />
      </>
    );
  }
);
